import React, { useState, useCallback, useEffect } from "react";
import useDarkMode from '../../../hooks/useDarkMode';
import useThemeColor from "../../../hooks/useThemeColor";
import "react-datetime/css/react-datetime.css";
import {
  Panel,
  PanelHeader,
  PanelBody,
} from "../../../components/panel/panel.jsx";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import CategoryModal from "../../../components/CustomComponents/modal/parentCategoryModel.jsx";
import Preloader from "../../../components/CustomComponents/preLoad/preLoader.js";
import axiosInstance from "../../../config/axiosApi/axiosApi.jsx";
import APIs from "../../../Constants/apis.js";
import showErrorToast, {
  showSuccessToast,
} from "../../../components/CustomComponents/toast/toast.js";
import ToggleSwitch from "../../../components/CustomComponents/Switchery/switchery.jsx";
import FormInput from "../../../components/CustomComponents/Input/formInput.jsx";
import CommonTimeSlotForAllWeek from "../../../components/CustomComponents/TimeSchedule/commonTimeSlotForAllWeek.js";
import ScheduleManager from "../../../components/CustomComponents/TimeSchedule/weeklySlots.js";
import schedule from "../../../components/CustomComponents/TimeSchedule/schedule.js";
import CategoryIconSelection from "../categoryIcon.js";
import CategoryDepartmentsModal from "../../../components/CustomComponents/modal/rootCategoryGroup.jsx";
import ReportingCategoryModel from "../../../components/CustomComponents/modal/reportingCategory";
import { appSettings } from "../../../app/appSettingsSlice";

function AddCategory() {
  const compID = useSelector((state) => state.auth.user).userData?.compID;
  const navigate = useNavigate();
  const { rootCatID } = useParams();
  const [categoryName, setCategoryName] = useState("");
  const [selectedParentCategory, setSelectedParentCategory] = useState({});
  const [selectedCategoryDepartments, setSelectedCategoryDepartments] = useState([]);
  const [selectedReportingCategory, setSelectedReportingCategory] = useState({});
  const [enableCategory, setEnableCategory] = useState(true);
  const [regularHours, setRegularHours] = useState(true);
  const [applyAllPlatforms, setApplyAllPlatforms] = useState(true);
  const [selectedIcon, setSelectedIcon] = useState(null);
  const [loading, setLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [showParentCategoryModal, setShowParentCategoryModal] = useState(false);
  const [showCategoryDepartmentsModal, setShowCategoryDepartmentsModal] = useState(false);
  const [showReportingCategoryModal, setShowReportingCategoryModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [reportingCategories, setReportingCategories] = useState([]);
  const [platforms, setPlatforms] = useState({
    epos: true,
    kiosk: true,
    online: true,
    delivery: true,
  });
  const [isAddOnCategory, setIsAddOnCategory] = useState(false);
  const [useSameTimeSlots, setUseSameTimeSlots] = useState(true);
  const [commonTimeSlot, setCommonTimeSlot] = useState([
    {
      from: "00:00",
      to: "23:59",
    },
  ]);
  const [schedules, setSchedules] = useState(schedule);

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  
  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();


  const applyCommonTimeSlotToAll = () => {
    const updatedSchedules = schedules.map((schedule) => ({
      ...schedule,
      timeSlots: commonTimeSlot.map((slot) => ({ ...slot })),
    }));
    setSchedules(updatedSchedules);
  };

  const handleSave = async () => {
    if (!categoryName.trim()) {
      showErrorToast("Category name is required");
      return;
    }

    if (selectedCategoryDepartments.length === 0) {
      showErrorToast("Category department is required");
      return;
    }

    setLoading(true);

    try {
      const formattedSchedules = [];
      if (!regularHours) {
        if (useSameTimeSlots) {
          const days = [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday",
          ];
          days.forEach((day) => {
            if (commonTimeSlot.length > 0) {
              commonTimeSlot.forEach((slot) => {
                formattedSchedules.push({
                  ID: 0,
                  CompID: compID,
                  RootCat_ID: 0,
                  DayName: day,
                  OpenTime: moment(slot.from, ["h:mm A"]).format("HH:mm"),
                  CloseTime: moment(slot.to, ["h:mm A"]).format("HH:mm"),
                  UpdateDateTime: new Date().toISOString(),
                });
              });
            }
          });
        } else {
          schedules.forEach((schedule) => {
            if (schedule.checked && schedule.timeSlots.length > 0) {
              schedule.timeSlots.forEach((slot) => {
                if (
                  moment(slot.from, ["h:mm A"]).isValid() &&
                  moment(slot.to, ["h:mm A"]).isValid()
                ) {
                  formattedSchedules.push({
                    ID: 0,
                    CompID: compID,
                    RootCat_ID: 0,
                    DayName: schedule.day,
                    OpenTime: moment(slot.from, ["h:mm A"]).format("HH:mm"),
                    CloseTime: moment(slot.to, ["h:mm A"]).format("HH:mm"),
                    UpdateDateTime: new Date().toISOString(),
                  });
                }
              });
            }
          });
        }
      }

      const payload = {
        rootCat_ID: 0,
        localizeID: "en",
        compID,
        rootCat_Name: categoryName,
        rootCat_Detail: "",
        rootCat_Image: selectedIcon || "",
        rootCat_Publish: enableCategory,
        rootCat_Orderby: 0,
        rG_ID: 1,
        rootCat_IsSubCategory: selectedParentCategory?.rootCat_ID ? true : false,
        rootCat_ParentRootCat_ID: selectedParentCategory?.rootCat_ID || 0,
        rootCat_ColorCode: "",
        pCategoryName: selectedParentCategory?.rootCat_Name || "",
        updateDateTime: new Date().toISOString(),
        isActiveforTill: platforms.epos,
        isActiveforKIOSK: platforms.kiosk,
        isActiveforTakeAway: platforms.online,
        isActiveforDelivery: platforms.delivery,
        isAddOnCategory: isAddOnCategory,
        isAvailableInTimeIntervals: regularHours,
        products: 0,
        subCategories: 0,
        subCategoriesList: [],
        RootCategoryWeeklyTimings: formattedSchedules,
        categoryDepartments: selectedCategoryDepartments.map((dept) => ({
          cD_ID: dept.cD_ID,
          cD_Name: dept.cD_Name,
          compID: compID,
        })),
        CategoryDepartmentRelations: selectedCategoryDepartments.map((dept) => ({
          rootCat_ID: 0,
          cD_ID: dept.cD_ID,
          compID: compID,
        })),
        rootCat_Type: selectedReportingCategory?.rC_ID || 1, // Use rC_ID as RootCat_Type, default to 1 (Food)
      };

      const response = await axiosInstance.post(APIs.ADD_CATEGORY, payload);
      if (response.data.status) {
        showSuccessToast("Category added successfully");
        navigate("/categories/manageCategories");
      } else {
        showErrorToast(response.data.message || "Failed to add category");
      }
    } catch (error) {
      showErrorToast(
        error.response?.data?.message ||
          "An error occurred while adding the category"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleParentCategorySelection = (selectedCategory) => {
    if (selectedCategory?.id === "none") {
      setSelectedParentCategory({});
    } else {
      setSelectedParentCategory(selectedCategory);
    }
    setShowParentCategoryModal(false);
  };

  const handleCategoryDepartmentsSelection = (selectedDepartments) => {
    setSelectedCategoryDepartments(selectedDepartments);
    setShowCategoryDepartmentsModal(false);
  };

  const handleReportingCategorySelection = (selectedCategory) => {
    setSelectedReportingCategory(selectedCategory);
    setShowReportingCategoryModal(false);
  };

  const handlePlatformChange = (platform) => {
    setPlatforms({
      ...platforms,
      [platform]: !platforms[platform],
    });
  };

  const fetchCategories = useCallback(async () => {
    if (!compID) {
      return;
    }

    try {
      setCategoriesLoading(true);
      const response = await axiosInstance.get(APIs.GET_CATEGORIES, {
        params: {
          compID,
          SortBy: "RootCat_Name",
          SortOrder: "ASC",
          Search: "",
        },
      });
      const categoryData = response.data.data || response.data;

      if (Array.isArray(categoryData)) {
        setCategories(categoryData);
      } else {
        setCategories([]);
      }
      const parentCategory = categoryData.find(
        (e) => e.rootCat_ID == rootCatID
      );
      if (parentCategory) {
        handleParentCategorySelection(parentCategory);
      }
    } catch (err) {
      setCategories([]);
    } finally {
      setCategoriesLoading(false);
      setLoading(false);
    }
  }, [compID, rootCatID]);

  const fetchReportingCategories = useCallback(async () => {
    if (!compID) {
      return;
    }
    try {
      const response = await axiosInstance.get(APIs.GET_ALL_CATEGORY_TYPES, {
        params: {
          compID: compID,
        },
      });
      const categoryData = response.data.data || response.data;
      const reportingCategoriesData = Array.isArray(categoryData) ? categoryData : [];
      setReportingCategories(reportingCategoriesData);
      
      // Auto-select the first reporting category if none is selected
      if (reportingCategoriesData.length > 0 && !selectedReportingCategory?.rC_ID) {
        setSelectedReportingCategory(reportingCategoriesData[0]);
      }
    } catch (err) {
      setReportingCategories([]);
    }
  }, [compID, selectedReportingCategory]);

  useEffect(() => {
    fetchCategories();
    fetchReportingCategories();
  }, [fetchCategories, fetchReportingCategories]);

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <Link to="/categories">Manage Categories</Link>
            </li>
            <li className="breadcrumb-item active">Add Categories</li>
          </ol>
          <h1 className="page-header mb-0">Add Category</h1>
        </div>

        <div className="ms-auto">
          <button 
            onClick={handleSave} 
            className="btn btn-switchery px-4"
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: `1px solid ${themeColor}`,
              boxShadow: `0 4px 15px ${themeColor}40`,
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = `0 6px 20px ${themeColor}60`;
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = `0 4px 15px ${themeColor}40`;
            }}
          >
            <i className="fa-lg me-2 ms-n2 text-white"></i>
            Submit
          </button>
        </div>
      </div>
      <div className="card border-0">
        <Panel className="m-3">
          <PanelHeader>General</PanelHeader>
          <PanelBody>
            <div className="bg-white p-4 rounded-3 shadow-sm">
              {/* Top Section: Category Name, Category Type and Toggle Switches */}
              <div className="row mb-4 g-3">
                {/* Icon Selection - Hidden for now */}
                <div className="col-md-3 text-center" style={{ display: 'none' }}>
                  <CategoryIconSelection
                    selectedIcon={selectedIcon}
                    setSelectedIcon={setSelectedIcon}
                  />
                </div>
                
                {/* Left 6 columns: Category Name and Category Type */}
                <div className="col-md-6">
                  <div className="row g-3">
                    <div className="col-12">
                      <FormInput
                        label="Category Name *"
                        value={categoryName}
                        onchange={(e) => setCategoryName(e.target.value)}
                        type="text"
                        placeHolder="Enter Category Name"
                        name="categoryName"
                        required
                      />
                    </div>
                    <div className="col-12">
                      <div className="position-relative">
                        <label 
                          className="form-label fw-semibold mb-2"
                          style={{ color: isDarkMode ? '#e2e8f0' : '#212529' }}
                        >
                          Category Type
                        </label>
                        <div
                          className="form-control d-flex align-items-center justify-content-between cursor-pointer"
                          style={{
                            minHeight: "48px",
                            cursor: "pointer",
                            borderColor: isDarkMode ? '#4a5568' : '#e9ecef',
                            backgroundColor: isDarkMode ? '#2d3748' : '#ffffff',
                            color: isDarkMode ? '#e2e8f0' : '#495057'
                          }}
                          onClick={() => setShowReportingCategoryModal(true)}
                        >
                          <div className="d-flex align-items-center flex-grow-1">
                            {selectedReportingCategory?.rC_Name ? (
                              <span style={{ color: isDarkMode ? '#e2e8f0' : '#212529' }}>
                                {selectedReportingCategory.rC_Name}
                              </span>
                            ) : (
                              <span style={{ color: isDarkMode ? '#a0aec0' : '#6c757d' }}>
                                Click to select category type
                              </span>
                            )}
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            {selectedReportingCategory?.rC_ID && (
                              <button
                                className="btn btn-link p-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedReportingCategory({});
                                }}
                                style={{ 
                                  fontSize: "16px",
                                  color: isDarkMode ? '#a0aec0' : '#6c757d'
                                }}
                              >
                                <i className="fa fa-times"></i>
                              </button>
                            )}
                            <i
                              className={`fa ${
                                showReportingCategoryModal
                                  ? "fa-chevron-up"
                                  : "fa-chevron-down"
                              }`}
                              style={{ color: isDarkMode ? '#a0aec0' : '#6c757d' }}
                            ></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Right 6 columns: Toggle switches */}
                <div className="col-md-6">
                  <div className="row row-cols-1 row-cols-sm-2 g-3">
                    {[
                      { label: "Enable Category", field: "enableCategory", checked: enableCategory, toggle: () => setEnableCategory(!enableCategory) },
                      // { label: "KIOSK", field: "kiosk", checked: platforms.kiosk, toggle: () => handlePlatformChange("kiosk") },
                      { label: "Online", field: "online", checked: platforms.online, toggle: () => handlePlatformChange("online") },
                      { label: "Delivery", field: "delivery", checked: platforms.delivery, toggle: () => handlePlatformChange("delivery") },
                      { label: "E POS", field: "epos", checked: platforms.epos, toggle: () => handlePlatformChange("epos") },
                      { label: "Add On Category", field: "addOnCategory", checked: isAddOnCategory, toggle: () => setIsAddOnCategory(!isAddOnCategory) },
                    ].filter(item => item).map(({ label, field, checked, toggle }) => (
                      <div key={field} className="col">
                        <ToggleSwitch
                          isChecked={checked}
                          onToggle={toggle}
                          label={label}
                          name={field}
                          themeColor={themeColor}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Form Fields Section */}
              <div className="row g-4">
                {/* Parent Category and Category Department */}
                <div className="col-md-6">
                  <div className="position-relative">
                    <label 
                      className="form-label fw-semibold mb-2"
                      style={{ color: isDarkMode ? '#e2e8f0' : '#212529' }}
                    >
                      Parent Category
                    </label>
                    <div
                      className="form-control d-flex align-items-center justify-content-between cursor-pointer"
                      style={{
                        minHeight: "48px",
                        cursor: "pointer",
                        borderColor: isDarkMode ? '#4a5568' : '#e9ecef',
                        backgroundColor: isDarkMode ? '#2d3748' : '#ffffff',
                        color: isDarkMode ? '#e2e8f0' : '#495057'
                      }}
                      onClick={() => setShowParentCategoryModal(true)}
                    >
                      <div className="d-flex align-items-center flex-grow-1">
                        {selectedParentCategory?.rootCat_Name ? (
                          <span style={{ color: isDarkMode ? '#e2e8f0' : '#212529' }}>
                            {selectedParentCategory.rootCat_Name}
                          </span>
                        ) : (
                          <span style={{ color: isDarkMode ? '#a0aec0' : '#6c757d' }}>
                            Click to select parent category
                          </span>
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        {selectedParentCategory?.rootCat_Name && (
                          <button
                            className="btn btn-link p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedParentCategory({});
                            }}
                            style={{ 
                              fontSize: "16px",
                              color: isDarkMode ? '#a0aec0' : '#6c757d'
                            }}
                          >
                            <i className="fa fa-times"></i>
                          </button>
                        )}
                        <i
                          className={`fa ${
                            showParentCategoryModal
                              ? "fa-chevron-up"
                              : "fa-chevron-down"
                          }`}
                          style={{ color: isDarkMode ? '#a0aec0' : '#6c757d' }}
                        ></i>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="position-relative">
                    <label 
                      className="form-label fw-semibold mb-2"
                      style={{ color: isDarkMode ? '#e2e8f0' : '#212529' }}
                    >
                      Category Department *
                    </label>
                    <div
                      className="form-control d-flex align-items-center justify-content-between cursor-pointer"
                      style={{
                        minHeight: "48px",
                        cursor: "pointer",
                        borderColor: isDarkMode ? '#4a5568' : '#e9ecef',
                        backgroundColor: isDarkMode ? '#2d3748' : '#ffffff',
                        color: isDarkMode ? '#e2e8f0' : '#495057'
                      }}
                      onClick={() => setShowCategoryDepartmentsModal(true)}
                    >
                      <div className="d-flex align-items-center flex-grow-1">
                        {selectedCategoryDepartments.length > 0 ? (
                          <span style={{ color: isDarkMode ? '#e2e8f0' : '#212529' }}>
                            {selectedCategoryDepartments[0].cD_Name}
                          </span>
                        ) : (
                          <span style={{ color: isDarkMode ? '#a0aec0' : '#6c757d' }}>
                            Click to select department
                          </span>
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        {selectedCategoryDepartments.length > 0 && (
                          <button
                            className="btn btn-link p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCategoryDepartments([]);
                            }}
                            style={{ 
                              fontSize: "16px",
                              color: isDarkMode ? '#a0aec0' : '#6c757d'
                            }}
                          >
                            <i className="fa fa-times"></i>
                          </button>
                        )}
                        <i
                          className={`fa ${
                            showCategoryDepartmentsModal
                              ? "fa-chevron-up"
                              : "fa-chevron-down"
                          }`}
                          style={{ color: isDarkMode ? '#a0aec0' : '#6c757d' }}
                        ></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Time Scheduling Section */}
              <div className="row g-4 mt-2">
                <div className="col-12">
                  <div className="card p-3">
                    <h5 className="card-title mb-3">Time Scheduling</h5>
                    <div className="row g-3 d-flex align-items-center p-4">
                      <div className="col-6">
                        <ToggleSwitch
                          isChecked={regularHours}
                          onToggle={() => setRegularHours(!regularHours)}
                          label="Available in regular Business Hours"
                          name="regularHours"
                          themeColor={themeColor}
                        />
                      </div>
                    </div>

                    {!regularHours && (
                      <>
                        <div className="row g-3 d-flex align-items-center p-4">
                          <div className="col-6">
                            <ToggleSwitch
                              isChecked={applyAllPlatforms}
                              onToggle={() => setApplyAllPlatforms(!applyAllPlatforms)}
                              label="Apply Same Times on Epos, Online, Delivery"
                              name="applyAllPlatforms"
                              themeColor={themeColor}
                            />
                          </div>
                        </div>

                        <hr />

                        <div className="row g-3 d-flex align-items-center p-4">
                          <div className="col-6">
                            <ToggleSwitch
                              isChecked={useSameTimeSlots}
                              onToggle={() => {
                                setUseSameTimeSlots(!useSameTimeSlots);
                                if (!useSameTimeSlots) {
                                  applyCommonTimeSlotToAll();
                                }
                              }}
                              label="Use same time slots for all days"
                              name="useSameTimeSlots"
                              themeColor={themeColor}
                            />
                          </div>
                        </div>
                        <hr />

                        {useSameTimeSlots ? (
                          <CommonTimeSlotForAllWeek
                            setCommonTimeSlots={setCommonTimeSlot}
                            commonTimeSlots={commonTimeSlot}
                          />
                        ) : (
                          <ScheduleManager
                            schedules={schedules}
                            setSchedules={setSchedules}
                             isDarkMode={isDarkMode}
                            themeColor={themeColor}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <CategoryModal
              showParentCategoryModal={showParentCategoryModal}
              setShowParentCategoryModal={setShowParentCategoryModal}
              showButton={true}
              setSelectedParentCategory={setSelectedParentCategory}
              selectedParentCategory={selectedParentCategory}
              onSubmit={handleParentCategorySelection}
              categories={categories}
              categoriesLoading={categoriesLoading}
            />
            <CategoryDepartmentsModal
              showModal={showCategoryDepartmentsModal}
              setShowModal={setShowCategoryDepartmentsModal}
              onSubmit={handleCategoryDepartmentsSelection}
              selectedGroups={selectedCategoryDepartments}
              setSelectedGroups={setSelectedCategoryDepartments}
              subtitle="Select a category department for this category (only 1 allowed)"
            />
            <ReportingCategoryModel
              isModalOpen={showReportingCategoryModal}
              setIsModalOpen={setShowReportingCategoryModal}
              onCategorySubmit={handleReportingCategorySelection}
              compID={compID}
              selectedCategory={selectedReportingCategory}
              categories={reportingCategories}
              showButton={true}
            />
          </PanelBody>
        </Panel>
      </div>
    </div>
  );
}

export default AddCategory;